import DesktopCourseDetails from 'components/CourseSettings/CourseDetails/DesktopCourseDetails';
import MobileCourseDetails from 'components/CourseSettings/CourseDetails/MobileCourseDetails';
import TabletCourseDetails from 'components/CourseSettings/CourseDetails/TabletCourseDetails';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import courseRequests from 'services/course';
import useFormValidation from 'services/hooks/useFormValidation';
import { INSTITUTIONS, MAJORS, SEMESTER_OPTIONS } from 'utils/constants';

const LIST_OF_SCHOOLS = [...INSTITUTIONS, { name: 'Other' }].map(({ name }) => ({ key: name, value: name }));
const LIST_OF_SEMESTERS = SEMESTER_OPTIONS.map(({ key, value }) => ({ key, value }));
const LIST_OF_MAJORS = MAJORS.map(({ name }) => ({ key: name, value: name }));

const CourseDetails = ({ setSelected, setAlertDialog, setOnBeforeChange }) => {
    const { auth } = useAuth();
    const { course, isLoading, updateCourse } = useApp();
    const { device } = useViewport();
    const { message } = useSnackbar();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const defaultFormValues = {
        school: LIST_OF_SCHOOLS.find(({ value }) => value === course?.school) || { key: 'Other', value: 'Other' },
        schoolOther: course?.school,
        faculty: course?.faculty,
        name: course?.name,
        section: course?.section,
        semester: LIST_OF_SEMESTERS.find(({ value }) => value === course?.semester) || null,
        field_of_study: LIST_OF_MAJORS.find(({ value }) => value === course?.field_of_study) || null,
    };
    const [form, setForm] = useState(defaultFormValues);
    const [isDirty, setIsDirty] = useState(false);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const getErrorMessage = (key, value, form) => {
        if (['schoolOther', 'faculty', 'name'].includes(key)) value = value?.trim();
        switch (key) {
            case 'schoolOther':
                return form.school.value === 'Other' && !value ? 'Please enter your school' : '';
            case 'faculty':
                return value ? '' : 'Please enter your faculty';
            case 'name':
                return value ? '' : 'Please enter your course';            
            case 'semester':
                return value ? '' : 'Please select your semester';
            case 'field_of_study':
                return value ? '' : 'Please select your field of study';
            default:
                return '';
        }
    };

    const handleSettingDropDown = ({ key }) => {
        switch (key) {
            case 'delete-course': {
                return openDeleteCourseDialog(course);
            }
            default:
                return;
        }
    };

    const { errors, validate, validateAll } = useFormValidation(form, getErrorMessage);

    const handleChange = (key, value) => {
        setForm((current) => ({ ...current, [key]: value }));
        setIsDirty(true);
        if (hasClickedSubmit || errors[key]) {
            validate(key, value);
        }
    };

    const deleteCourse = async () => {
        await courseRequests.deleteCourse(auth.course_id, { course_id: auth.course_id });
        history.push('/');
        return message.success(`${_.capitalize(course.name || 'The new course')} was deleted successfully.`);
    };

    const openDeleteCourseDialog = (course) => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: `Delete ${course.name || 'this course'}?`,
            description:
                'This course will be deleted permanently from our system, and users will lose access immediately. Please note that this action cannot be undone.',
            variant: 'destructive',
            actionButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            onAction: () => {
                closeDialog();
                return deleteCourse();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        }));
    };

    const updateCourseSettings = async (form) => {
        const updatedCourse = {
            school: form.school,
            faculty: form.faculty,
            name: form.name,
            semester: form.semester,
            section: form.section,
            field_of_study: form.field_of_study,
        };

        await updateCourse(updatedCourse);
        message.success('Course details updated successfully.');
    };

    const submit = async () => {
        if (!hasClickedSubmit) setHasClickedSubmit(true);
        if (!validateAll()) return;
        await updateCourseSettings({
            faculty: form.faculty,
            field_of_study: form.field_of_study.value,
            name: form.name,
            school: form.school.value === 'Other' ? form.schoolOther : form.school.value,
            section: form.section,
            semester: form.semester.value,
        });
        setIsDirty(false);
    };

    const reset = () => {
        setForm(defaultFormValues);
        setHasClickedSubmit(false);
        setIsDirty(false);
    };

    useEffect(() => {
        if (course) setForm(defaultFormValues);
    }, [course]);

    useEffect(() => {
        if (!isDirty) validateAll();
    }, [isDirty]);

    const props = {
        loading: isLoading,
        form,
        isDirty,
        errors,
        onChange: handleChange,
        onSubmit: submit,
        onCancel: reset,
        handleSettingDropDown,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: (
                <MobileCourseDetails
                    {...props}
                    setSelected={(value) => {
                        if (isDirty) {
                            setAlertDialog((current) => ({
                                ...current,
                                open: true,
                                message: 'Leave Page?',
                                description: 'If you leave this page, your changes will be lost.',
                                onAction: () => {
                                    setSelected(value);
                                    setAlertDialog((current) => ({ ...current, open: false }));
                                },
                            }));
                        } else {
                            setSelected(value);
                        }
                    }}
                />
            ),
            tablet: <TabletCourseDetails {...props} />,
            desktop: <DesktopCourseDetails {...props} />,
        }[device];
    };

    return <>{renderResponsiveLayout(device)}</>;
};

export default CourseDetails;
